import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Trophy, ArrowRight, Robot, Circle, Copy, Globe, User, Share, Star, GitFork, TrendUp, ChartBar, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import BacktestModal from "@/components/BacktestModal";
import { SymbolLogo } from "@/components/SymbolSearchInput";

const STRATEGY_LABEL = {
  ma_crossover: "MA Crossover",
  rsi: "RSI",
  macd: "MACD",
  bollinger: "Bollinger",
};

function buildCopyHref(row) {
  const p = new URLSearchParams({
    copy: "1",
    symbol: row.symbol,
    strategy: row.strategy,
    from_bot: row.bot_id, // Copy History link
  });
  Object.entries(row.params || {}).forEach(([k, v]) => {
    if (v !== null && v !== undefined) p.set(k, String(v));
  });
  return `/bots?${p.toString()}`;
}

async function shareStrategy(row) {
  const url = `${window.location.origin}${buildCopyHref(row)}`;
  // Fire-and-forget share-count beacon (best-effort, no user impact if it fails)
  api.post(`/leaderboard/share/${row.bot_id}`).catch(() => {});
  const shareData = {
    title: `STRATOS — ${row.symbol} ${row.strategy}`,
    text: `Check out this ${row.symbol} ${row.strategy} bot on STRATOS — ${row.win_rate}% win rate.`,
    url,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
      toast.success("Shared");
      return;
    }
  } catch (_) {
    // user cancelled — fall through to clipboard
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard");
  } catch {
    toast.error("Could not copy link — long-press to copy manually");
  }
}

export default function LeaderboardPage() {
  const [mineRows, setMineRows] = useState([]);
  const [globalRows, setGlobalRows] = useState([]);
  const [copiedRows, setCopiedRows] = useState([]);
  const [followingRows, setFollowingRows] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [trending, setTrending] = useState([]);
  const [tagFilter, setTagFilter] = useState(null); // null = 'all'
  const [allTags, setAllTags] = useState([]);
  const [tab, setTab] = useState("mine"); // 'mine' | 'global' | 'following' | 'copies'
  const [loading, setLoading] = useState(true);

  // Inline backtest state
  const [backtestOpen, setBacktestOpen] = useState(false);
  const [backtestConfig, setBacktestConfig] = useState(null);

  useEffect(() => {
    api.get("/leaderboard/tags").then((r) => setAllTags(r.data.tags || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const tagQuery = tagFilter ? `&tag=${tagFilter}` : "";
        const [mine, pub, copies, feat, trend, following] = await Promise.all([
          api.get("/leaderboard"),
          api.get(`/leaderboard/public?limit=50&min_sells=1${tagQuery}`),
          api.get("/bots/copies"),
          api.get("/leaderboard/featured?days=7"),
          api.get(`/leaderboard/trending?days=7&limit=5${tagQuery}`),
          api.get("/leaderboard/following?limit=50"),
        ]);
        setMineRows(mine.data.rows || []);
        setGlobalRows(pub.data.rows || []);
        setCopiedRows(copies.data.copies || []);
        setFeatured(feat.data.featured || null);
        setTrending(trend.data.symbols || []);
        setFollowingRows(following.data.rows || []);
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [tagFilter]);

  const rows = tab === "mine" ? mineRows : tab === "global" ? globalRows : tab === "following" ? followingRows : [];

  const openBacktest = (row) => {
    setBacktestConfig({ symbol: row.symbol, strategy: row.strategy, quantity: 1, ...(row.params || {}) });
    setBacktestOpen(true);
  };

  const active = mineRows.filter((r) => r.enabled);
  const totalRealized = mineRows.reduce((s, r) => s + r.total_realized_pl, 0);
  const totalTrades = mineRows.reduce((s, r) => s + r.num_trades, 0);
  const overallWinRate = (() => {
    const w = mineRows.reduce((s, r) => s + r.wins, 0);
    const l = mineRows.reduce((s, r) => s + r.losses, 0);
    return w + l > 0 ? (w / (w + l)) * 100 : 0;
  })();

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="leaderboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Automation</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2 flex items-center gap-3">
            <Trophy size={30} weight="regular" className="text-[#00C805]" />
            Bot leaderboard
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Every bot ranked by realized P/L. Switch to <span className="text-white">Global</span> to
            benchmark against every trader on STRATOS and copy the top performers.
          </p>
        </div>
        <Link
          to="/bots"
          data-testid="leaderboard-back-btn"
          className="mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-zinc-800 rounded-sm px-3 py-2 flex items-center gap-1"
        >
          <Robot size={12} /> Manage bots
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        {[
          { id: "mine", label: "My bots", Icon: User, count: mineRows.length },
          { id: "global", label: "Global", Icon: Globe, count: globalRows.length },
          { id: "following", label: "Following", Icon: UserPlus, count: followingRows.length },
          { id: "copies", label: "My copies", Icon: GitFork, count: copiedRows.length },
        ].map(({ id, label, Icon, count }) => (
          <button
            key={id}
            data-testid={`leaderboard-tab-${id}`}
            onClick={() => setTab(id)}
            className={`mono text-[10px] uppercase tracking-widest px-4 py-2.5 flex items-center gap-2 border-b-2 transition-colors ${
              tab === id
                ? "text-white border-[#00C805]"
                : "text-zinc-500 border-transparent hover:text-zinc-300"
            }`}
          >
            <Icon size={12} weight={tab === id ? "fill" : "regular"} />
            {label} <span className="text-zinc-600">({count})</span>
          </button>
        ))}
      </div>

      {/* Tag filter — Global tab only */}
      {tab === "global" && allTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="tag-filter-strip">
          <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Playstyle:</span>
          <button
            onClick={() => setTagFilter(null)}
            data-testid="tag-filter-all"
            className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
              tagFilter === null
                ? "text-black bg-[#00C805] border-[#00C805]"
                : "text-zinc-400 border-zinc-800 hover:border-zinc-500 hover:text-white"
            }`}
          >
            All
          </button>
          {allTags.map((t) => (
            <button
              key={t}
              onClick={() => setTagFilter(t)}
              data-testid={`tag-filter-${t}`}
              className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm border transition-colors ${
                tagFilter === t
                  ? "text-black bg-[#00C805] border-[#00C805]"
                  : "text-zinc-400 border-zinc-800 hover:border-zinc-500 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* Trending strip — Global tab only */}
      {tab === "global" && trending.length > 0 && (
        <div
          data-testid="trending-strip"
          className="border border-zinc-800 rounded-sm bg-zinc-900/40 p-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={16} weight="fill" className="text-[#00C805]" />
            <div className="mono text-[10px] text-zinc-400 uppercase tracking-widest">
              Trending on STRATOS · last 7 days
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {trending.map((t, idx) => (
              <Link
                key={t.symbol}
                to={`/trade?s=${t.symbol}`}
                data-testid={`trending-chip-${t.symbol}`}
                className="group inline-flex items-center gap-2 border border-zinc-800 hover:border-[#00C805] rounded-sm px-3 py-2 transition-colors"
              >
                <span className="mono text-[10px] text-zinc-500">#{idx + 1}</span>
                <span className="font-semibold text-white">{t.symbol}</span>
                <span className="mono text-[10px] text-zinc-500 group-hover:text-zinc-300 uppercase tracking-widest">
                  {t.copies > 0 && `${t.copies} ${t.copies === 1 ? "copy" : "copies"}`}
                  {t.copies > 0 && t.shares > 0 && " · "}
                  {t.shares > 0 && `${t.shares} share${t.shares === 1 ? "" : "s"}`}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Featured this week — only on Global tab */}
      {tab === "global" && featured && (
        <div
          data-testid="featured-card"
          className="relative overflow-hidden border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-zinc-900 to-zinc-900 rounded-sm p-5"
        >
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/15 rounded-sm">
                <Star size={20} weight="fill" className="text-amber-400" />
              </div>
              <div>
                <div className="mono text-[10px] text-amber-300 uppercase tracking-widest">
                  Featured this week · {featured.featured_window_days}d P/L
                </div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-2xl font-bold">{featured.symbol}</span>
                  <span className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                    {STRATEGY_LABEL[featured.strategy] || featured.strategy}
                  </span>
                  <span className="mono text-[11px] text-zinc-400">by {featured.owner_tag}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">7-day P/L</div>
                <div className="mono text-2xl font-bold profit">
                  {fmtMoney(featured.featured_pl, { showSign: true })}
                </div>
              </div>
              <div className="text-right">
                <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Lifetime</div>
                <div className={`mono text-lg ${featured.total_realized_pl >= 0 ? "profit" : "loss"}`}>
                  {fmtMoney(featured.total_realized_pl, { showSign: true })}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => openBacktest(featured)}
                  data-testid="featured-backtest-btn"
                  className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-sm px-2.5 py-1.5"
                  title="Backtest before you copy"
                >
                  <ChartBar size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => shareStrategy(featured)}
                  data-testid="featured-share-btn"
                  className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-500 rounded-sm px-2.5 py-1.5"
                >
                  <Share size={12} />
                </button>
                <Link
                  to={buildCopyHref(featured)}
                  data-testid="featured-copy-btn"
                  className="inline-flex items-center gap-1 text-xs text-black bg-amber-400 hover:bg-amber-300 border border-amber-400 rounded-sm px-3 py-1.5 font-semibold"
                >
                  <Copy size={12} weight="bold" /> Copy
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overall stats — only shown on 'mine' tab */}
      {tab === "mine" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Active bots</div>
            <div className="mono text-3xl font-bold mt-2">{active.length}<span className="text-zinc-600 text-lg">/{mineRows.length}</span></div>
          </div>
          <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Bot-executed trades</div>
            <div className="mono text-3xl font-bold mt-2">{totalTrades}</div>
          </div>
          <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Total realized P/L</div>
            <div className={`mono text-3xl font-bold mt-2 ${totalRealized >= 0 ? "profit" : "loss"}`}>
              {fmtMoney(totalRealized, { showSign: true })}
            </div>
          </div>
          <div className="p-5 bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Overall win rate</div>
            <div className={`mono text-3xl font-bold mt-2 ${overallWinRate >= 50 ? "profit" : "loss"}`}>
              {fmtPct(overallWinRate)}
            </div>
          </div>
        </div>
      )}

      {tab === "copies" ? (
        <Card>
          <CardHeader title="Strategies you've copied from the community" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="copies-table">
              <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-5 py-2.5">Your bot</th>
                  <th className="text-left px-3 py-2.5">Copied from</th>
                  <th className="text-right px-3 py-2.5">Your P/L</th>
                  <th className="text-right px-3 py-2.5">Source P/L</th>
                  <th className="text-right px-3 py-2.5">Δ vs source</th>
                  <th className="text-right px-5 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="mono">
                {copiedRows.map((r) => {
                  const delta = r.delta_vs_source;
                  const positive = (delta ?? 0) >= 0;
                  return (
                    <tr
                      key={r.bot_id}
                      className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                      data-testid={`copies-row-${r.symbol}`}
                    >
                      <td className="px-5 py-3">
                        <div className="font-semibold text-base">{r.symbol}</div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          {STRATEGY_LABEL[r.strategy] || r.strategy}
                          {r.enabled ? " · active" : " · paused"}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        {r.source.exists ? (
                          <>
                            <div className="text-xs text-zinc-200">{r.source.owner_tag}</div>
                            <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                              {r.source.symbol} · {STRATEGY_LABEL[r.source.strategy] || r.source.strategy}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-zinc-600">source bot deleted</span>
                        )}
                      </td>
                      <td className={`text-right px-3 py-3 ${r.my_realized_pl >= 0 ? "profit" : "loss"}`}>
                        {fmtMoney(r.my_realized_pl, { showSign: true })}
                      </td>
                      <td className={`text-right px-3 py-3 ${r.source.exists ? (r.source.realized_pl >= 0 ? "profit" : "loss") : "text-zinc-600"}`}>
                        {r.source.exists ? fmtMoney(r.source.realized_pl, { showSign: true }) : "—"}
                      </td>
                      <td className={`text-right px-3 py-3 text-base font-bold ${
                        delta === null ? "text-zinc-600" : positive ? "profit" : "loss"
                      }`}>
                        {delta === null ? "—" : `${positive ? "▲" : "▼"} ${fmtMoney(Math.abs(delta), { showSign: false })}`}
                      </td>
                      <td className="text-right px-5 py-3">
                        <Link
                          to={`/trade?s=${r.symbol}`}
                          className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805]"
                        >
                          Trade <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
                {!loading && copiedRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center mono text-xs text-zinc-600">
                      You haven't copied any public strategies yet.
                      <br />
                      Head to the <button
                        onClick={() => setTab("global")}
                        className="text-white underline mt-2 inline-block"
                      >
                        Global tab
                      </button> and hit <Copy size={11} className="inline mx-0.5" /> Copy on any bot.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
      <Card>
        <CardHeader
          title={tab === "mine" ? "Ranked by realized P/L" : "Global — anonymized, community-wide"}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-2.5">Rank</th>
                <th className="text-left px-3 py-2.5">Bot</th>
                {tab === "mine" ? (
                  <th className="text-left px-3 py-2.5">Status</th>
                ) : (
                  <th className="text-left px-3 py-2.5">Trader</th>
                )}
                <th className="text-right px-3 py-2.5">Trades</th>
                <th className="text-right px-3 py-2.5">Win rate</th>
                {tab === "mine" && <th className="text-right px-3 py-2.5">Avg win / loss</th>}
                <th className="text-right px-3 py-2.5">Best / worst</th>
                <th className="text-right px-3 py-2.5">Realized P/L</th>
                <th className="text-right px-5 py-2.5">Action</th>
              </tr>
            </thead>
            <tbody className="mono">
              {rows.map((r, idx) => {
                const positive = r.total_realized_pl >= 0;
                return (
                  <tr
                    key={r.bot_id}
                    className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                    data-testid={`leaderboard-row-${tab}-${r.symbol}`}
                  >
                    <td className="px-5 py-3">
                      <div
                        className={`text-lg font-bold ${
                          idx === 0
                            ? "text-[#00C805]"
                            : idx === 1
                            ? "text-zinc-300"
                            : idx === 2
                            ? "text-amber-500"
                            : "text-zinc-500"
                        }`}
                      >
                        #{idx + 1}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2.5">
                        <SymbolLogo
                          symbol={r.symbol}
                          logo={`https://financialmodelingprep.com/image-stock/${r.symbol}.png`}
                          size={28}
                        />
                        <div className="min-w-0">
                          {tab !== "mine" ? (
                            <Link
                              to={`/b/${r.bot_id}`}
                              data-testid={`leaderboard-profile-${r.symbol}`}
                              className="hover:text-[#00C805]"
                            >
                              <div className="font-semibold text-base">{r.symbol}</div>
                              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                                {STRATEGY_LABEL[r.strategy] || r.strategy}
                              </div>
                            </Link>
                          ) : (
                            <>
                              <div className="font-semibold text-base">{r.symbol}</div>
                              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                                {STRATEGY_LABEL[r.strategy] || r.strategy}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    {tab === "mine" ? (
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <Circle
                            size={8}
                            weight="fill"
                            className={r.enabled ? "text-[#00C805] pulse-dot" : "text-zinc-700"}
                          />
                          <span className="text-[10px] uppercase tracking-widest text-zinc-400">
                            {r.enabled ? "Active" : "Paused"}
                          </span>
                        </div>
                      </td>
                    ) : (
                      <td className="px-3 py-3">
                        <Link
                          to={`/t/${r.owner_tag}`}
                          className="text-[11px] text-zinc-400 hover:text-white hover:underline"
                          data-testid={`leaderboard-trader-link-${r.symbol}`}
                        >
                          {r.owner_tag}
                        </Link>
                        {r.share_count > 0 && (
                          <div
                            data-testid={`leaderboard-share-count-${r.symbol}`}
                            className="flex items-center gap-1 text-[10px] text-zinc-500 mt-0.5"
                          >
                            <Share size={9} /> {r.share_count} share{r.share_count === 1 ? "" : "s"}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="text-right px-3 py-3">
                      {r.num_trades}
                      <div className="text-[10px] text-zinc-500">
                        {(r.num_buys ?? Math.max(0, r.num_trades - r.num_sells))}B · {r.num_sells}S
                      </div>
                    </td>
                    <td
                      className={`text-right px-3 py-3 ${
                        r.win_rate >= 50 ? "profit" : r.num_sells === 0 ? "text-zinc-600" : "loss"
                      }`}
                    >
                      {r.num_sells > 0 ? fmtPct(r.win_rate) : "—"}
                      <div className="text-[10px] text-zinc-500">
                        {r.wins}W · {r.losses}L
                      </div>
                    </td>
                    {tab === "mine" && (
                      <td className="text-right px-3 py-3 text-xs">
                        <div className="profit">
                          {r.avg_win !== 0 ? fmtMoney(r.avg_win, { showSign: true }) : "—"}
                        </div>
                        <div className="loss">
                          {r.avg_loss !== 0 ? fmtMoney(r.avg_loss, { showSign: true }) : "—"}
                        </div>
                      </td>
                    )}
                    <td className="text-right px-3 py-3 text-xs">
                      <div className="profit">
                        {r.best_trade !== 0 ? fmtMoney(r.best_trade, { showSign: true }) : "—"}
                      </div>
                      <div className="loss">
                        {r.worst_trade !== 0 ? fmtMoney(r.worst_trade, { showSign: true }) : "—"}
                      </div>
                    </td>
                    <td
                      className={`text-right px-3 py-3 text-lg font-bold ${
                        positive ? "profit" : "loss"
                      }`}
                    >
                      {r.num_sells > 0 ? (
                        fmtMoney(r.total_realized_pl, { showSign: true })
                      ) : (
                        <span className="text-zinc-600 text-sm">no data</span>
                      )}
                    </td>
                    <td className="text-right px-5 py-3">
                      {tab === "mine" ? (
                        <Link
                          to={`/trade?s=${r.symbol}`}
                          className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805]"
                        >
                          Trade <ArrowRight size={12} />
                        </Link>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <button
                            type="button"
                            onClick={() => openBacktest(r)}
                            data-testid={`leaderboard-backtest-${r.symbol}`}
                            title="Backtest before you copy"
                            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-500 rounded-sm px-2.5 py-1.5 transition-colors"
                          >
                            <ChartBar size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => shareStrategy(r)}
                            data-testid={`leaderboard-share-${r.symbol}`}
                            title="Copy share link"
                            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white border border-zinc-800 hover:border-zinc-500 rounded-sm px-2.5 py-1.5 transition-colors"
                          >
                            <Share size={12} />
                          </button>
                          <Link
                            to={buildCopyHref(r)}
                            data-testid={`leaderboard-copy-${r.symbol}`}
                            className="inline-flex items-center gap-1 text-xs text-white hover:text-[#00C805] border border-zinc-800 hover:border-[#00C805] rounded-sm px-2.5 py-1.5 transition-colors"
                          >
                            <Copy size={12} /> Copy
                          </Link>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={tab === "mine" ? 9 : 8} className="px-5 py-16 text-center mono text-xs text-zinc-600">
                    {tab === "mine" ? (
                      <>
                        No bots deployed yet.
                        <br />
                        <Link to="/bots" className="text-white underline mt-2 inline-block">
                          Create your first bot →
                        </Link>
                      </>
                    ) : tab === "following" ? (
                      <>
                        You're not following any traders yet.
                        <br />
                        Switch to the <button
                          onClick={() => setTab("global")}
                          className="text-white underline mt-2 inline-block"
                        >
                          Global tab
                        </button> and hit a trader's tag or the Follow button on their bot page.
                      </>
                    ) : (
                      <>
                        Community leaderboard is still warming up.
                        <br />
                        Bots need at least one closed trade to appear here.
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      )}
      <BacktestModal open={backtestOpen} onOpenChange={setBacktestOpen} config={backtestConfig} />
    </div>
  );
}
